/**
 * Reset Password Page
 */

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authService } from "../services/authApi";

export const ResetPassword: React.FC = () => {
	const [searchParams] = useSearchParams();
	const [token, setToken] = useState<string>(searchParams.get("token") ?? "");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const paramToken = searchParams.get("token");
		if (paramToken) {
			setToken(paramToken);
		}
	}, [searchParams]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (!token.trim()) {
			setError("Reset token is missing. Please use the link from your email.");
			return;
		}
		if (password.length < 8) {
			setError("Password must be at least 8 characters long.");
			return;
		}
		if (password !== confirmPassword) {
			setError("Passwords do not match.");
			return;
		}

		setLoading(true);
		try {
			await authService.resetPassword(token.trim(), password);
			setSuccess(true);
		} catch (err: any) {
			setError(err.message || "Failed to reset password");
		} finally {
			setLoading(false);
		}
	};

	if (success) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
					<div className="text-center">
						<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
							<svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<h2 className="mt-4 text-2xl font-bold text-gray-900">Password updated</h2>
						<p className="mt-2 text-sm text-gray-600">
							Your password has been reset successfully. You can now log in with your new password.
						</p>
						<div className="mt-6">
							<Link to="/login" className="text-sm font-medium text-teal-600 hover:text-teal-500">
								Back to login
							</Link>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
				<div>
					<h2 className="text-3xl font-bold text-gray-900">Set a new password</h2>
					<p className="mt-2 text-sm text-gray-600">
						Choose a new password for your account.
					</p>
				</div>

				<form className="mt-8 space-y-6" onSubmit={handleSubmit}>
					{error && (
						<div className="rounded-md bg-red-50 border border-red-200 p-4">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					)}

					<div>
						<label htmlFor="password" className="block text-sm font-medium text-gray-700">
							New Password<span className="text-red-500">*</span>
						</label>
						<input
							id="password"
							name="password"
							type="password"
							minLength={8}
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
							placeholder="Enter a new password"
						/>
					</div>

					<div>
						<label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
							Confirm Password<span className="text-red-500">*</span>
						</label>
						<input
							id="confirmPassword"
							name="confirmPassword"
							type="password"
							minLength={8}
							required
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
							placeholder="Re-enter your new password"
						/>
					</div>

					<div>
						<button
							type="submit"
							disabled={loading}
							className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-700 hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{loading ? "Updating password..." : "Reset password"}
						</button>
					</div>

					<div className="text-center">
						<Link to="/login" className="text-sm font-medium text-teal-600 hover:text-teal-500">
							Back to login
						</Link>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ResetPassword;
